import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Package, RefreshCw } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import { warehouseService, type PackageListItem } from "@/services/warehouseService";
import { usePageTitle } from "@/hooks/usePageTitle";

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || "";
const DEFAULT_CENTER: [number, number] = [-1.5491, 53.8008];
const DELIVERY_STATUSES = "delivery_booked,shipped,delivered";

const markerColour: Record<string, string> = {
  delivery_booked: "#a855f7",
  shipped: "#3b82f6",
};

type Coordinates = { latitude: number; longitude: number };
const postcodeCache = new Map<string, Coordinates | null>();

async function geocodePostcode(postcode: string): Promise<Coordinates | null> {
  const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.replace(/\s/g, ""))}`);
  if (!response.ok) return null;
  const data = await response.json();
  if (!data.result?.latitude || !data.result?.longitude) return null;
  return { latitude: data.result.latitude, longitude: data.result.longitude };
}

export default function DeliveryManagement() {
  usePageTitle("Delivery Map");
  const mapElement = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [deliveries, setDeliveries] = useState<PackageListItem[]>([]);
  const [coordinates, setCoordinates] = useState<Record<number, Coordinates>>({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | "delivery_booked" | "shipped" | "delivered">("all");

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const result = await warehouseService.list({
        warehouse_status: DELIVERY_STATUSES,
        sort_by: "delivery_booked_at",
        sort_order: "desc",
        limit: 100,
      });
      setDeliveries(result.data);
    } catch (error) {
      console.error("Failed to load delivery map:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  const visibleDeliveries = useMemo(
    () => statusFilter === "all" ? deliveries : deliveries.filter((delivery) => delivery.warehouse_status === statusFilter),
    [deliveries, statusFilter],
  );

  useEffect(() => {
    if (!MAPBOX_ACCESS_TOKEN || !mapElement.current || map.current) return;
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    map.current = new mapboxgl.Map({
      container: mapElement.current,
      style: "mapbox://styles/mapbox/dark-v11",
      center: DEFAULT_CENTER,
      zoom: 5.5,
    });
    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadCoordinates() {
      const next: Record<number, Coordinates> = {};
      for (const delivery of visibleDeliveries) {
        const postcode = delivery.shipping_code?.trim();
        if (!postcode) continue;
        if (!postcodeCache.has(postcode)) postcodeCache.set(postcode, await geocodePostcode(postcode));
        const point = postcodeCache.get(postcode);
        if (point) next[delivery.id] = point;
      }
      if (!cancelled) setCoordinates(next);
    }

    if (visibleDeliveries.length) loadCoordinates();
    return () => { cancelled = true; };
  }, [visibleDeliveries]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;

    markers.current.forEach((marker) => marker.remove());
    markers.current = [];
    const bounds = new mapboxgl.LngLatBounds();
    let mappedCount = 0;

    for (const delivery of visibleDeliveries) {
      const point = coordinates[delivery.id];
      if (!point) continue;
      const popup = new mapboxgl.Popup({ offset: 20 }).setHTML(`
        <div class="text-sm">
          <strong>${delivery.packing_number}</strong><br />
          ${delivery.customer_name || "Unknown customer"}<br />
          <span>${delivery.shipping_city || ""} ${delivery.shipping_code || ""}</span>
        </div>
      `);
      const marker = new mapboxgl.Marker({ color: markerColour[delivery.warehouse_status] || "#64748b" })
        .setLngLat([point.longitude, point.latitude])
        .setPopup(popup)
        .addTo(instance);
      markers.current.push(marker);
      bounds.extend([point.longitude, point.latitude]);
      mappedCount += 1;
    }

    if (mappedCount === 1) instance.flyTo({ center: bounds.getCenter(), zoom: 11 });
    if (mappedCount > 1) instance.fitBounds(bounds, { padding: 72, maxZoom: 11 });
  }, [coordinates, visibleDeliveries]);

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Delivery Map"
        count={visibleDeliveries.length}
        subtitle="Map delivery status and location"
        breadcrumbs={[{ label: "Warehouse", href: "/shipping/deliveries" }, { label: "Delivery Map" }]}
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-border p-0.5">
              {[
                ["all", "All"],
                ["delivery_booked", "Booked"],
                ["shipped", "Shipped"],
                ["delivered", "Delivered"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStatusFilter(value as typeof statusFilter)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium ${statusFilter === value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={loadDeliveries}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        }
      />

      {!MAPBOX_ACCESS_TOKEN ? (
        <div className="flex min-h-[420px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card p-8 text-center">
          <Package size={28} className="mb-3 text-muted-foreground" />
          <p className="font-medium">Mapbox is not configured</p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">Set VITE_MAPBOX_ACCESS_TOKEN in frontend environment to show active delivery locations.</p>
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-xl border border-border">
          <div ref={mapElement} className="h-[calc(100vh-250px)] min-h-[420px]" />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-sm text-muted-foreground">
              <RefreshCw size={16} className="mr-2 animate-spin" /> Loading deliveries
            </div>
          )}
        </div>
      )}
    </div>
  );
}
